import { useIntl } from "react-intl";

import styles from "@components/banners/Banner.module.css";
import ActionIconButton from "@components/buttons/ActionIconButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import ScheduleConsultButton from "@components/ScheduleConsultButton";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables
} from "@typing/Generated";
import { Banner as BannerType } from "@typing/State";
import { imageUrl } from "@utils/imageUtils";

type Props = {
  banner: BannerType;
  borderRadius?: boolean;
};

const Banner = ({ banner, borderRadius }: Props) => {
  const globalOpener = useGlobalOpener(banner.openerKind);
  const intl = useIntl();

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    "appEvent"
  );

  const handleButtonClickThrough = () => {
    if (banner.bannerId) {
      createAppEvent({
        actionKind: AppEventActionKindEnum.banner_click_through,
        resourceId: banner.bannerId
      });
    }
  };

  const handleButtonClick = () => {
    handleButtonClickThrough();

    if (banner.openerKind) {
      globalOpener.open();
    } else {
      banner.buttonOnClick?.();
    }
  };

  return (
    <div className={`${styles.banner} ${styles[banner.color]} ${borderRadius ? styles.borderRadius : ""}`}>
      <div className={styles.bannerContent}>
        <div className={styles.group + " " + styles.text}>
          <div>
            {banner.image ? (
              <div className={styles.imageWrapper}>
                <img
                  alt={intl.formatMessage({ id: `accessibility.ariaLabels.${banner.image}` })}
                  className={styles.image}
                  src={imageUrl(banner.image)}
                />
              </div>
            ) : (
              <div className={styles.infoIcon}>
                <Icon color="white" icon={banner.icon ?? "infoCircle"} spacingEnd />
              </div>
            )}
          </div>
          <div>
            <BodyCopy bold>{banner.text}</BodyCopy>
            {banner.subText && (
              <div>
                <BodyCopy bold size="small">
                  {banner.subText}
                </BodyCopy>
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttonAndDismiss}>
          <div className={styles.group}>
            {banner.scheduleConsultButton && banner.journeyId ? (
              <ScheduleConsultButton
                color="pink"
                journeyId={banner.journeyId}
                onClick={handleButtonClickThrough}
                size="small"
              />
            ) : banner.buttonTextKey ? (
              <PrimaryButton
                color="pink"
                disabled={banner.buttonDisabled}
                onClick={handleButtonClick}
                routerLink={banner.routerLink}
                size="small"
                textKey={banner.buttonTextKey}
              />
            ) : null}
          </div>
        </div>
      </div>
      {banner.onDismiss && (
        <div className={styles.dismissGroup}>
          <div className={styles.divider} />
          <div className={styles.close}>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.close"
              color="white"
              icon="xmark"
              onClick={banner.onDismiss}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Banner;
